import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, ShieldCheck, Maximize2, Minimize2 } from "lucide-react";

// Ronda 31.7: id monotónico para claves estables en la lista de mensajes.
// Antes usábamos `map((m, i) => key={i})` que puede causar bugs si en el
// futuro se inserta/elimina un mensaje intermedio.
let _msgSeq = 1;
const nextMsgId = () => `m-${Date.now()}-${_msgSeq++}`;

const getSessionId = () => {
  // Conversación nueva en cada visita: Sol no arrastra conversaciones anteriores.
  let s = sessionStorage.getItem("bea_chat_session");
  if (!s) {
    s = crypto.randomUUID();
    sessionStorage.setItem("bea_chat_session", s);
  }
  return s;
};

const loadProfile = () => {
  try { return JSON.parse(localStorage.getItem("bea_sol_profile") || "null"); } catch (e) { return null; }
};

// Basic Spanish mobile phone validation: 9 digits, may include +34 / 0034 / spaces
const normalizeES = (raw) => {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  let d = digits;
  if (d.startsWith("0034")) d = d.slice(4);
  else if (d.length === 11 && d.startsWith("34")) d = d.slice(2);
  if (d.length !== 9) return null;
  return `+34${d}`;
};

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  // Ronda 33.23 · La clienta puede ampliar la ventana de conversación a
  // pantalla completa cuando la conversación se alarga y el tamaño por
  // defecto se le queda pequeño. Se conserva la opción de volver al
  // tamaño normal y de cerrar.
  const [expanded, setExpanded] = useState(false);
  const [texts, setTexts] = useState({});
  const [profile, setProfile] = useState(loadProfile);
  const [formName, setFormName] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formError, setFormError] = useState("");
  const [messages, setMessages] = useState(() => {
    const p = loadProfile();
    return p ? [{ id: "greeting-0", role: "assistant", text: `Hola, ${p.name}. Soy Sol, la asistente de Bea. ¿En qué puedo ayudarte?` }] : [];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [needsPhone, setNeedsPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phone, setPhone] = useState(() => loadProfile()?.phone || localStorage.getItem("bea_chat_phone") || "");
  const [pendingUserMsg, setPendingUserMsg] = useState(null); // last question that triggered needs_phone
  // Ronda 33.15 · Si el envío al backend falla (timeout, red, 5xx),
  // guardamos el texto que la clienta había escrito y pintamos un botón
  // «Reintentar». No lo perdemos ni la mandamos a un callejón sin salida.
  const [lastFailedMsg, setLastFailedMsg] = useState(null);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  // Cada visita (conversación nueva) queda asociada al nombre y teléfono del formulario.
  useEffect(() => {
    if (profile) {
      api.post("/chat/start", { session_id: sessionId.current, name: profile.name, phone: profile.phone }).catch(() => {});
    }
  }, [profile]);

  useEffect(() => {
    // Ronda 33.17 · El saludo es fijo por decisión de producto — no lo
    // sobreescribimos con el campo `chatbot_greeting` del admin panel para
    // evitar variantes ("Beatriz" vs "Bea de Ibiza") que aparecían según
    // qué versión estuviera guardada en config. Sí seguimos leyendo el
    // resto de textos editables.
    api.get("/config/public")
      .then(({ data }) => {
        const ct = data?.content_texts || {};
        setTexts(ct);
      })
      .catch((err) => {
        console.warn("ChatBot: no se pudieron cargar los textos personalizados", err);
      });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, needsPhone]);

  const sendWithText = async (userMsg, opts = {}) => {
    setLoading(true);
    setLastFailedMsg(null);
    try {
      const body = { session_id: sessionId.current, message: userMsg };
      if (profile?.name) body.client_name = profile.name;
      const p = opts.phone || phone;
      if (p) body.phone = p;
      const { data } = await api.post("/chat", body);
      setMessages((m) => [...m, { id: nextMsgId(), role: "assistant", text: data.reply }]);
      if (data.needs_phone) {
        setNeedsPhone(true);
        setPendingUserMsg(userMsg);
      } else {
        setNeedsPhone(false);
        setPendingUserMsg(null);
      }
    } catch (e) {
      // Ronda 33.15 · Fallo de red/timeout/5xx. Guardamos el mensaje para
      // que la clienta pueda reintentar sin volver a escribirlo. En el
      // input ponemos el texto por si prefiere editarlo antes.
      const isTimeout = e?.code === "ECONNABORTED";
      const errMsg = isTimeout
        ? "Ay, la respuesta se está tardando más de la cuenta. Puedes reintentar o reformularlo."
        : "Ahora mismo no puedo procesar tu mensaje. Puedes reintentar en un momento.";
      setMessages((m) => [
        ...m,
        { id: nextMsgId(), role: "assistant", text: errMsg, error: true },
      ]);
      setLastFailedMsg({ text: userMsg, phone: opts.phone || phone || undefined });
      setInput(userMsg);
    }
    setLoading(false);
  };

  const retryLastFailed = async () => {
    if (!lastFailedMsg || loading) return;
    const { text, phone: p } = lastFailedMsg;
    setInput("");
    await sendWithText(text, p ? { phone: p } : {});
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { id: nextMsgId(), role: "user", text: userMsg }]);
    setInput("");
    await sendWithText(userMsg);
  };

  const submitPhone = async () => {
    const norm = normalizeES(phoneInput);
    if (!norm) {
      setMessages((m) => [...m, { id: nextMsgId(), role: "assistant", text: "Ese número no me cuadra. Escríbelo con 9 dígitos (con o sin +34)." }]);
      return;
    }
    // Show phone acknowledgement as the user bubble
    setMessages((m) => [...m, { id: nextMsgId(), role: "user", text: norm }]);
    setPhone(norm);
    localStorage.setItem("bea_chat_phone", norm);
    setPhoneInput("");
    setNeedsPhone(false);
    // Replay the pending intent question WITH the phone so the backend returns the real status
    const replay = pendingUserMsg || "estado de mi cita";
    await sendWithText(replay, { phone: norm });
  };

  const startWithSol = () => {
    const n = formName.trim();
    const p = normalizeES(formPhone);
    if (n.length < 2) { setFormError("Escribe tu nombre."); return; }
    if (!p) { setFormError("Ese teléfono no me cuadra. Escríbelo con 9 dígitos (con o sin +34)."); return; }
    const prof = { name: n, phone: p };
    localStorage.setItem("bea_sol_profile", JSON.stringify(prof));
    localStorage.setItem("bea_chat_phone", p);
    setProfile(prof);
    setPhone(p);
    setFormError("");
    setMessages([{ id: "greeting-0", role: "assistant", text: `Encantada, ${n}. ¿En qué puedo ayudarte?` }]);
  };

  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return null;

  return (
    <>
      {/* Ronda 33.17 · Burbuja de acceso más grande y visible + ventana
          ampliada. La burbuja pasa de 56×56 a 72×72 px y suma un anillo
          dorado sutil para que se distinga como punto de contacto. */}
      <div className="fixed bottom-20 right-6 z-40 flex items-center gap-3">
        {!open && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="hidden sm:flex bg-white border border-[#B8912E]/40 rounded-full px-4 py-2 shadow-lg text-sm text-[#0A0A0A] font-medium"
            data-testid="chatbot-invite"
          >
            ¿Tienes alguna duda? Sol te ayuda
          </motion.div>
        )}
        <motion.button
          data-testid="chatbot-toggle"
          onClick={() => setOpen((o) => !o)}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          className="w-[72px] h-[72px] rounded-full bg-[#B8912E] text-black shadow-[0_10px_36px_rgba(184,145,46,0.45)] ring-2 ring-[#F5E7B8]/60 flex items-center justify-center"
          aria-label={open ? "Cerrar chat" : "Abrir chat"}
        >
          {open ? <X size={28}/> : <MessageCircle size={28}/>}
        </motion.button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={
              expanded
                ? "fixed inset-4 sm:inset-10 z-40 flex flex-col bg-white border border-[#B8912E]/40 rounded-2xl overflow-hidden shadow-[0_20px_80px_rgba(184,145,46,0.35)]"
                : "fixed bottom-[112px] right-6 z-40 w-[calc(100vw-2rem)] sm:w-[440px] max-h-[80vh] sm:max-h-[78vh] flex flex-col bg-white border border-[#B8912E]/40 rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(184,145,46,0.24)]"
            }
            data-testid="chatbot-panel"
          >
            <div className="p-4 border-b border-[#B8912E]/20 flex items-center justify-between bg-white">
              <div>
                <div className="font-serif text-lg text-[#B8912E]">Dudas frecuentes</div>
                <div className="text-xs text-[#6B6B6B]">Respondo al momento</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowQR(true)}
                  data-testid="chat-whatsapp-qr"
                  className="text-xs flex items-center gap-1 gold hover:text-[#8F6F1F] transition-colors"
                >
                  <MessageCircle size={12}/> WhatsApp
                </button>
                {/* Ronda 33.23 · Botón para ampliar / restaurar el chat. */}
                <button
                  onClick={() => setExpanded((v) => !v)}
                  data-testid="chat-toggle-expand"
                  aria-label={expanded ? "Reducir chat" : "Ampliar chat a pantalla completa"}
                  className="w-8 h-8 rounded-full text-[#6B6B6B] hover:text-[#0A0A0A] hover:bg-[#F5E7B8]/30 flex items-center justify-center transition-colors"
                >
                  {expanded ? <Minimize2 size={16}/> : <Maximize2 size={16}/>}
                </button>
                {/* Ronda 33.17 · Aspa de cierre siempre visible en la cabecera. */}
                <button
                  onClick={() => { setOpen(false); setExpanded(false); }}
                  data-testid="chat-close-header"
                  aria-label="Cerrar chat"
                  className="w-8 h-8 rounded-full text-[#6B6B6B] hover:text-[#0A0A0A] hover:bg-[#F5E7B8]/30 flex items-center justify-center transition-colors"
                >
                  <X size={18}/>
                </button>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
              {!profile && (
                <div className="space-y-3" data-testid="sol-intro-form">
                  <div className="max-w-[90%] px-3 py-2 rounded-2xl text-sm bg-[#FFF7E5] border border-[#B8912E]/25 text-[#0A0A0A]">
                    Hola, soy Sol, la asistente de Bea. Antes de empezar, ¿me dices tu nombre y tu teléfono? Así Bea sabrá con quién he hablado.
                  </div>
                  <input className="field text-sm" placeholder="Nombre" value={formName} onChange={(e) => setFormName(e.target.value)} data-testid="sol-form-name" />
                  <input className="field text-sm" type="tel" inputMode="tel" placeholder="Teléfono" value={formPhone} onChange={(e) => setFormPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && startWithSol()} data-testid="sol-form-phone" />
                  {formError && <div className="text-xs text-red-700" data-testid="sol-form-error">{formError}</div>}
                  <button onClick={startWithSol} className="btn-primary text-sm w-full" data-testid="sol-form-start">Empezar a hablar con Sol</button>
                  <a href="/privacidad" target="_blank" rel="noreferrer" className="block text-[11px] text-[#6B6B6B] underline" data-testid="sol-form-privacy-link">Política de privacidad</a>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={m.id || `idx-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-[#FFF7E5] border border-[#B8912E]/25 text-[#0A0A0A]"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && <div className="text-xs text-[#6B6B6B]">Escribiendo...</div>}

              {/* Ronda 33.15 · Botón Reintentar tras un fallo (timeout/red). */}
              {lastFailedMsg && !loading && (
                <div className="mt-2 flex justify-start" data-testid="chat-retry-container">
                  <button
                    onClick={retryLastFailed}
                    className="btn-primary text-xs px-3 py-1.5"
                    data-testid="chat-retry-btn"
                  >
                    Reintentar
                  </button>
                </div>
              )}

              {needsPhone && !loading && (
                <div className="mt-2 p-3 rounded-xl border border-[#B8912E]/30 bg-[#FFF7E5]" data-testid="chat-phone-request">
                  <div className="text-xs text-[#6B6B6B] mb-2">Solo lo uso para consultar tu reserva. No se comparte.</div>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      inputMode="tel"
                      autoFocus
                      className="field text-sm"
                      placeholder="+34 6XX XXX XXX"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submitPhone()}
                      data-testid="chat-phone-input"
                    />
                    <button
                      onClick={submitPhone}
                      disabled={!phoneInput.trim() || loading}
                      className="btn-primary text-sm px-3"
                      data-testid="chat-phone-submit"
                    >Enviar</button>
                  </div>
                </div>
              )}
            </div>
            {profile && <div className="p-3 border-t border-[#B8912E]/20 flex gap-2 bg-white">
              <input
                data-testid="chat-input"
                className="field text-sm"
                placeholder="Escribe tu pregunta..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                disabled={loading}
              />
              <button onClick={send} disabled={loading} data-testid="chat-send" className="btn-primary px-4"><Send size={14}/></button>
            </div>}
            <div className="px-3 py-2 bg-[#FBF7EC] border-t border-[#B8912E]/15 flex items-start gap-2" data-testid="chat-privacy-notice">
              <ShieldCheck size={12} className="text-[#B8912E] mt-0.5 shrink-0"/>
              <p className="text-[10px] leading-snug text-[#6B6B6B]">
                {texts.landing_privacy_notice || "Guardamos tu conversación durante 12 meses para poder ayudarte mejor. Tus datos están protegidos conforme al RGPD."}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp QR Modal — works everywhere, no wa.me dependency */}
      <WhatsAppQRModal open={showQR} onClose={() => setShowQR(false)} />
    </>
  );
}

export function WhatsAppQRModal({ open, onClose }) {
  const phone = "34619240875";
  const waUrl = `https://wa.me/${phone}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(waUrl)}&color=0-0-0&bgcolor=255-255-255&margin=6`;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="whatsapp-qr-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl max-w-sm w-full p-8 relative text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid="qr-close"
              className="absolute top-3 right-3 text-black/60 hover:text-black transition-colors"
            >
              <X size={20}/>
            </button>
            <div className="font-serif text-2xl text-black">Dudas por WhatsApp</div>
            <p className="text-black/60 text-sm mt-2">Escanea el código o pulsa el botón</p>
            <div className="mt-6 mx-auto w-[220px] h-[220px] bg-white rounded-xl border border-black/10 p-2 flex items-center justify-center">
              <img
                src={qrSrc}
                alt="QR WhatsApp Bea de Ibiza"
                className="w-full h-full object-contain"
                data-testid="whatsapp-qr-image"
              />
            </div>
            <div className="mt-6">
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-direct-link"
                className="inline-block bg-[#25D366] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#20BA5A] transition-colors"
              >
                Abrir WhatsApp
              </a>
              <p className="mt-3 text-black/50 text-xs">
                También puedes escanear el QR desde otro dispositivo o continuar en WhatsApp Web.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
